import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable component that wraps a section of a form, providing consistent styling
 * with a title, border, padding, and background color.
 * Uses semantic styling and configuration support.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} props.title - The title to display at the top of the section.
 * @param {React.ReactNode} props.children - The content (form fields, etc.) to render within the section.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the wrapper div.
 * @returns {React.ReactElement} The rendered FormSection component.
 *
 * @example
 * <FormSection title="User Details">
 *   <InputField name="firstName" label="First Name" />
 *   <InputField name="lastName" label="Last Name" />
 * </FormSection>
 */
const FormSection = ({ title, children, className = '' }) => {
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    return (
        <div className={`p-0 ${className}`}>
            {title && (
                <h3 className={`text-lg font-semibold mb-3 ${semantic.contentCardHeading} pb-1`}>
                    {replace(title)}
                </h3>
            )}
            <div className="space-y-4">
                {children}
            </div>
        </div>
    );
};

FormSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

export default FormSection;

